import S from './core.js';
import { MONTH_NAMES as MONTHS } from './periods.js';
import { loadLogo } from './logo.js';

/* The heavy libraries are loaded only when someone actually exports. */
export async function makeExcel(layout) {
  const mod = await import('exceljs/dist/exceljs.min.js');
  return S.buildWorkbook(mod.default ?? mod, { ...layout, logo: layout.logo ?? await loadLogo() });
}
export async function makePdf(layout) {
  const { jsPDF } = await import('jspdf');
  const { applyPlugin } = await import('jspdf-autotable');
  applyPlugin(jsPDF);
  return S.buildPdf(jsPDF, { ...layout, logo: layout.logo ?? await loadLogo() });
}
/* The Summary as an Excel file: one row per report and month (as filtered on screen), one column per line item, and a total row. */
export async function makeSummaryExcel(view) {
  const mod = await import('exceljs/dist/exceljs.min.js');
  const ExcelJS = mod.default ?? mod;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Settlement Report Studio';
  const ws = wb.addWorksheet(`Summary ${view.year}`.slice(0, 31));
  const lead = 3, tail = 3, n = view.columns.length, last = lead + n + tail;
  const bold = { bold: true };
  const fill = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
  const when = (row) => (row ? `${row.actioned_by_name}, ${new Intl.DateTimeFormat('en-GB', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(row.actioned_at)).replace(',', '')}` : 'Pending');
  const numFmt = (kind) => (kind === 'int' ? '#,##0' : kind === 'pct' ? '0.0"%"' : '#,##0.00');
  const f = view.filters || {};
  const applied = [f.partner && `partner contains "${f.partner}"`, f.reportId && `report ID contains "${f.reportId}"`,
    f.month !== undefined && f.month !== 'all' && `month ${MONTHS[Number(f.month)]}`, f.statusLabel && `status ${f.statusLabel}`, f.frequencyLabel && `frequency ${f.frequencyLabel}`].filter(Boolean);

  ws.columns = [{ width: 14 }, { width: 30 }, { width: 12 }, ...view.columns.map(() => ({ width: 18 })), { width: 18 }, { width: 28 }, { width: 28 }];
  const titleRow = ws.addRow([view.title]);
  titleRow.height = 40;
  for (let i = 1; i <= last; i++) titleRow.getCell(i).fill = fill('FF191919');          // a black band, like the report header, so a light logo shows
  titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FFE1E1E1' } };
  titleRow.getCell(1).alignment = { vertical: 'middle' };
  S.placeLogo(wb, ws, view.logo ?? await loadLogo(), ws.columns.map((col) => col.width), 0, 0, 36, 53);   // top right, on a dark band behind it
  ws.addRow([applied.length ? `Filtered: ${applied.join(', ')}` : 'All reports and months']).font = { italic: true, color: { argb: 'FF666666' } };
  ws.addRow([]);

  const groupRow = ws.addRow([]);                      // section names over their columns
  let c = lead + 1;
  view.groups.forEach((g) => {
    groupRow.getCell(c).value = g.title;
    if (g.span > 1) ws.mergeCells(groupRow.number, c, groupRow.number, c + g.span - 1);
    c += g.span;
  });
  groupRow.getCell(lead + n + 1).value = 'Owner & approval';
  ws.mergeCells(groupRow.number, lead + n + 1, groupRow.number, last);
  for (let i = 1; i <= last; i++) { const cell = groupRow.getCell(i); cell.fill = fill('FF191919'); cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }; cell.alignment = { horizontal: 'center' }; }

  const head = ws.addRow(['Report ID', 'Partner', 'Month', ...view.columns.map((x) => x.label), 'Account manager', 'Finance approval', 'AM approval']);
  head.font = bold;
  head.eachCell((cell) => { cell.fill = fill('FFE1E1E1'); cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }; });
  head.height = 45;

  view.rows.forEach((r) => {
    const row = ws.addRow([r.id, r.partner, r.month, ...view.columns.map((x) => r.values[x.key]), r.manager, when(r.finance), when(r.account_manager)]);
    view.columns.forEach((x, i) => { const cell = row.getCell(lead + 1 + i); cell.numFmt = numFmt(x.kind); cell.alignment = { horizontal: 'right' }; });
  });
  const total = ws.addRow(['Total', '', '', ...view.columns.map((x) => (view.totals ? view.totals[x.key] : null)), '', '', '']);
  total.font = bold;
  for (let i = 1; i <= last; i++) { total.getCell(i).border = { top: { style: 'thin' } }; }
  view.columns.forEach((x, i) => { const cell = total.getCell(lead + 1 + i); cell.numFmt = numFmt(x.kind); cell.alignment = { horizontal: 'right' }; });

  ws.autoFilter = { from: { row: head.number, column: 1 }, to: { row: head.number, column: last } };
  ws.views = [{ state: 'frozen', xSplit: lead, ySplit: head.number }];
  ws.pageSetup = { orientation: 'landscape', paperSize: 9, fitToPage: true, fitToWidth: 1, fitToHeight: 0 };
  return wb.xlsx.writeBuffer();
}
export async function makeZip(files) {               // files: [{ name, data }]
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  files.forEach((f) => zip.file(f.name, f.data));
  return zip.generateAsync({ type: 'uint8array' });
}
export function saveFile(name, data, type) {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export const fileBase = (r) => `${r.settlement_report_id}_${r.report_year}_${r.as_at_date}`.replace(/[^A-Za-z0-9_.-]+/g, '_');
